import React, { useMemo } from 'react';
import { Box, Button } from '@mui/material';
import Card from './Card';
import SectionHeader from './SectionHeader';
import FilterDropdown from './FilterDropdown';
import { withLanguage } from '../common/Texts';
import { getText } from '../common/MultiLanguageText';
import { dashboardTokens as t } from './tokens';
import { POTENTIAL_LEVEL_CODES } from '../../Jacques';

// Las opciones salen del modelo de Jacques (ya vienen de menor a mayor: 1C, 1B,
// 1A, 2C...) para no dejar afuera niveles que el calculo de potencial si
// devuelve, como 6A.
const potentialOptions = POTENTIAL_LEVEL_CODES.map((code) => ({ value: code, text: code }));

function toOptions(arr, language) {
    return (arr || [])
        .map((x) => ({ value: x.id, text: getText(x, 'name', language) || String(x.id) }))
        .sort((a, b) => a.text.localeCompare(b.text));
}

export default function OrganizationFilters({
    language,
    buRange,
    areaRange,
    posRange,
    igRange,
    slideBU = [],
    slideArea = [],
    slidePos = [],
    slideIG = [],
    slidePotential = [],
    onChangeBU,
    onChangeArea,
    onChangePos,
    onChangeIG,
    onChangePotential,
    onClearAll,
}) {
    const tr = withLanguage(language);

    const buOptions = useMemo(() => toOptions(buRange, language), [buRange, language]);
    const areaOptions = useMemo(() => toOptions(areaRange, language), [areaRange, language]);
    const posOptions = useMemo(() => toOptions(posRange, language), [posRange, language]);
    const igOptions = useMemo(() => toOptions(igRange, language), [igRange, language]);

    // El filtro de Potencial es opcional: sólo se muestra donde el contenedor
    // lo maneja (Dashboard). La NineBox reutiliza estos filtros sin él.
    const showPotential = Boolean(onChangePotential);

    const hasAnyFilter =
        slideBU.length + slideArea.length + slidePos.length + slideIG.length
        + slidePotential.length > 0;

    return (
        <Card>
            <SectionHeader
                title={tr('filters')}
                action={
                    <Button
                        size="small"
                        onClick={onClearAll}
                        disabled={!hasAnyFilter}
                        sx={{
                            color: t.color.fgMuted,
                            fontSize: 13,
                            textTransform: 'none',
                            '&:hover': { color: t.color.primary, backgroundColor: 'transparent' },
                        }}
                    >
                        {tr('clear_all') || 'Clear all'}
                    </Button>
                }
            />
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: showPotential ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)',
                    lg: showPotential ? 'repeat(5, 1fr)' : 'repeat(4, 1fr)',
                },
                gap: 2,
            }}>
                <FilterDropdown
                    label={tr('business_unit')}
                    options={buOptions}
                    value={slideBU}
                    onChange={onChangeBU}
                    language={language}
                />
                <FilterDropdown
                    label={tr('area')}
                    options={areaOptions}
                    value={slideArea}
                    onChange={onChangeArea}
                    language={language}
                />
                <FilterDropdown
                    label={tr('position')}
                    options={posOptions}
                    value={slidePos}
                    onChange={onChangePos}
                    language={language}
                />
                <FilterDropdown
                    label={tr('starred_group')}
                    options={igOptions}
                    value={slideIG}
                    onChange={onChangeIG}
                    language={language}
                />
                {showPotential && (
                    <FilterDropdown
                        label={tr('potential_future_filter')}
                        options={potentialOptions}
                        value={slidePotential}
                        onChange={onChangePotential}
                        language={language}
                    />
                )}
            </Box>
        </Card>
    );
}
